import type { ProcessOptions, ProcessResult, SimpleInitOptions } from '../ndl-koten-ocr';

/**
 * Web Worker版のNDL Koten OCR
 * メインスレッドをブロックせずにOCR処理を実行できます
 */
export class NDLKotenOCRWorker {
  private worker: Worker | null = null;
  private messageId = 0;
  private pendingMessages = new Map<string, {
    resolve: (value: any) => void;
    reject: (error: Error) => void;
  }>();
  private progressCallbacks = new Map<string, (progress: number, message: string) => void>();

  /**
   * Workerを初期化します
   * @param workerUrl - Worker スクリプトのURL（省略時は自動的に生成）
   */
  constructor(workerUrl?: string | URL) {
    if (typeof Worker === 'undefined') {
      throw new Error('Web Workers are not supported in this environment');
    }

    // workerUrlが指定されていない場合は、バンドルされたWorkerを使用
    if (!workerUrl) {
      try {
        // ESM環境でのみ import.meta.url を使用
        if (typeof import.meta !== 'undefined' && import.meta.url) {
          // Webpack 5+ / Vite の場合
          this.worker = new Worker(new URL('./ocr.worker.ts', import.meta.url), {
            type: 'module'
          });
        } else {
          throw new Error('Worker URL must be provided when not using ESM format');
        }
      } catch (e) {
        // 動的インポートが失敗した場合のエラーメッセージ
        throw new Error(
          'Failed to create Web Worker. Please provide a Worker URL explicitly:\n' +
          'new NDLKotenOCRWorker("/path/to/ocr.worker.js")'
        );
      }
    } else {
      this.worker = new Worker(workerUrl);
    }

    this.setupWorkerListeners();
  }

  private setupWorkerListeners(): void {
    if (!this.worker) return;

    this.worker.addEventListener('message', (event) => {
      const { type, id, data, error } = event.data;
      const pending = this.pendingMessages.get(id);

      if (type === 'progress') {
        const callback = this.progressCallbacks.get(id);
        if (callback && data) {
          callback(data.progress, data.message);
        }
      } else if (pending) {
        if (type === 'success') {
          pending.resolve(data);
          this.pendingMessages.delete(id);
          this.progressCallbacks.delete(id);
        } else if (type === 'error') {
          pending.reject(new Error(error || 'Unknown error'));
          this.pendingMessages.delete(id);
          this.progressCallbacks.delete(id);
        }
      }
    });

    this.worker.addEventListener('error', (error) => {
      console.error('Worker error:', error);
      // すべての保留中のメッセージをエラーで解決
      this.pendingMessages.forEach((pending) => {
        pending.reject(new Error('Worker error occurred'));
      });
      this.pendingMessages.clear();
      this.progressCallbacks.clear();
    });
  }

  private postMessage<T = any>(type: string, data?: any): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Worker not initialized'));
        return;
      }

      const id = String(++this.messageId);
      this.pendingMessages.set(id, { resolve, reject });

      // 関数は送信できないので、データから除外
      let cleanData = data;
      if (data && typeof data === 'object') {
        cleanData = { ...data };
        // progressCallbackやonProgressなどの関数を除外
        for (const key in cleanData) {
          if (typeof cleanData[key] === 'function') {
            delete cleanData[key];
          }
        }
        // options内の関数も除外
        if (cleanData.options && typeof cleanData.options === 'object') {
          cleanData.options = { ...cleanData.options };
          for (const key in cleanData.options) {
            if (typeof cleanData.options[key] === 'function') {
              delete cleanData.options[key];
            }
          }
        }
      }

      this.worker.postMessage({ type, id, data: cleanData });
    });
  }

  /**
   * OCRエンジンを初期化します
   * @param options - 初期化オプション
   */
  async init(options?: SimpleInitOptions): Promise<void> {
    // progressCallbackを分離
    const callback = options?.progressCallback;
    const cleanOptions = options ? { ...options } : {};
    if ('progressCallback' in cleanOptions) {
      delete (cleanOptions as any).progressCallback;
    }

    // コールバックがある場合は登録
    if (callback) {
      const id = String(this.messageId + 1); // 次のIDを予測
      this.progressCallbacks.set(id, callback);
    }

    await this.postMessage('init', cleanOptions);
  }

  /**
   * 画像からテキストを認識します
   * @param imageData - 処理対象の画像データ（ImageData, HTMLImageElement, HTMLCanvasElement）
   * @param options - 処理オプション
   * @returns OCR処理結果
   */
  async process(
    imageData: ImageData | HTMLImageElement | HTMLCanvasElement,
    options?: ProcessOptions
  ): Promise<ProcessResult> {
    // HTMLImageElement や HTMLCanvasElement を ImageData に変換
    let processImageData: ImageData;

    if (imageData instanceof ImageData) {
      processImageData = imageData;
    } else {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to create canvas context');
      }

      if (imageData instanceof HTMLImageElement) {
        canvas.width = imageData.naturalWidth || imageData.width;
        canvas.height = imageData.naturalHeight || imageData.height;
        ctx.drawImage(imageData, 0, 0);
      } else {
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        ctx.drawImage(imageData, 0, 0);
      }

      processImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    }

    return await this.postMessage<ProcessResult>('process', {
      imageData: processImageData,
      options
    });
  }

  /**
   * Workerを終了してリソースを解放します
   */
  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingMessages.clear();
    this.progressCallbacks.clear();
  }

  /**
   * Workerが初期化されているかチェックします
   */
  isInitialized(): boolean {
    return this.worker !== null;
  }
}